import React, { useEffect, useState } from "react";
import WebSocketClient from "./ws/WebSocketClient";
import { _single } from "../../core/services/dataBean/EntityCatalog";
import { useParams } from "react-router-dom";
import { encryptData } from "../../utils/helpers";
import ChatRenderer from "./ChatRenderer";
import MarkdownMessage from "./MarkdownMessage";
import "./css/chat.css";
import "./css/chatHome.css";
import { env } from "../../env";
import TypingMessage from "./TypingMessage/TypingMessage";

export interface UIMessage {
    id: string;
    from: "user" | "system";
    payload: ChatMessage;
}

export type ChatMessage =
    | { type: "text"; response: string }
    | { type: "users_result"; data: User[]; meta: Meta };

export interface User {
    name: string;
    nit: string;
    id: number;
    email?: string;
}

export interface Meta {
    page: number;
    rows: number;
    totalPages: number;
    totalRecords: number;
}


const ws = new WebSocketClient({
    // url: "ws://192.168.1.127:8083/quickdataerp/chat",
    url: env.REACT_APP_ENDPOINT != '' ? env.REACT_APP_ENDPOINT.replace("https", "wss") + env.REACT_APP_DEPLOY + "/chat" : "ws://192.168.1.127:8083/quickdataerp/chat",
    debug: true
});

interface ComponentChatAI {
    Structure?: "Modal" | "MenuLogin"
}

export default function ChatAIWS({ Structure = "Modal" }: ComponentChatAI) {
    const miga = useParams();
    const { module, view, param } = miga;
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const chatHomeRef = React.useRef<HTMLDivElement>(null);

    const messagesContainerRef = React.useRef<HTMLDivElement>(null);
    const lastMessageRef = React.useRef<HTMLDivElement>(null);

    const [connected, setConnected] = useState(false);
    const [messages, setMessages] = useState<UIMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState("");
    const [hasStarted, setHasStarted] = useState(false);
    const [chatHomeHeight, setChatHomeHeight] = useState("92dvh");
    const [currentPage, setCurrentPage] = useState<any>({});

    useEffect(() => {
        if (param) {
            try {
                setCurrentPage(JSON.parse(atob(param)));
            } catch (error) {
                console.error("Error al decodificar el parámetro:", error);
            }
        }
    }, [param]);

    useEffect(() => {
        if (Structure !== "MenuLogin") return;

        const updateMenuLoginHeight = () => {
            const el = chatHomeRef.current;
            if (!el) return;

            const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const isMobile = window.matchMedia("(max-width: 768px)").matches;
            const baseDvh = Math.floor(viewportHeight * (isMobile ? 0.9 : 0.92));
            const top = el.getBoundingClientRect().top;
            const reservedBottom = 8;
            const availableHeight = Math.floor(viewportHeight - top - reservedBottom);
            const nextHeight = Math.max(320, Math.min(baseDvh, availableHeight));

            setChatHomeHeight(`${nextHeight}px`);
        };

        updateMenuLoginHeight();

        window.addEventListener("resize", updateMenuLoginHeight);
        window.visualViewport?.addEventListener("resize", updateMenuLoginHeight);

        return () => {
            window.removeEventListener("resize", updateMenuLoginHeight);
            window.visualViewport?.removeEventListener("resize", updateMenuLoginHeight);
        };
    }, [Structure]);

    useEffect(() => {
        const el = lastMessageRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            el.scrollIntoView({ behavior: "auto", block: "end" });
        });

        observer.observe(el);

        return () => observer.disconnect();
    }, [messages]);

    useEffect(() => {

        ws.onStatusChange(setConnected);

        ws.onMessage((msg) => {
            const parsed = formatResponse(msg);

            const systemMessage: UIMessage = {
                id: crypto.randomUUID(),
                from: "system",
                payload: parsed
            };

            setMessages(prev => [...prev, systemMessage]);
            setLoading(false);
        });

        ws.connect();

        return () => {
            ws.disconnect();
        };
    }, []);

    function formatResponse(raw: string): ChatMessage {
        if (!raw) {
            return {
                type: "text",
                response: ""
            };
        }

        try {
            const parsed = JSON.parse(raw);

            if (parsed?.type === "users_result" && Array.isArray(parsed.data)) {
                return parsed as ChatMessage;
            }

            return {
                type: "text",
                response: normalizeText(parsed.response)
            };

        } catch {
            return {
                type: "text",
                response: raw
            };
        }
    }

    function normalizeText(text: string): string {
        if (!text) return "";

        return text
            .replace(/\\n/g, "\n")
            .replace(/\\t/g, "\t")
            .trim();
    }



    const send = () => {

        setHasStarted(true);

        if (!input.trim() || loading) return;

        const userMessage: UIMessage = {
            id: crypto.randomUUID(),
            from: "user",
            payload: {
                type: "text",
                response: input
            }
        };

        setMessages(prev => [...prev, userMessage]);
        setLoading(true);

        ws.send({
            UserID: encryptData(_single.getAccountID() + ""),
            PageIDLn: currentPage.IDLn,
            Message: input
        });

        setInput("");


        setTimeout(() => {
            setLoading(false);
        }, 90000);
    };

    // const quickActions = [
    //     { label: "Crear imágenes", icon: "🖼️", prompt: "Quiero crear una imagen de: " },
    //     { label: "Aprender algo nuevo", icon: "📚", prompt: "Explícame de forma simple: " },
    //     { label: "Escribir o editar", icon: "✍️", prompt: "Ayúdame a redactar/mejorar este texto: " },
    //     { label: "How to help", icon: "💡", prompt: "¿Cómo puedes ayudarme con mi proyecto? " },
    // ];


    return Structure === "Modal" ?
        <div className="chat-wrapper">
            <div className="chat-header">
                <div className={`status-dot ${connected ? "on" : "off"}`} />
                <span className="status-text">
                    {connected ? "Conectado" : "Sin conexión"}
                </span>
            </div>

            <div className="chat-container" >
                {messages.map((m, idx) => {
                    const isLast = idx === messages.length - 1;

                    return (
                        <div key={m.id} ref={isLast ? lastMessageRef : null} className={`chat-bubble ${m.from}`}>
                            {m.payload.type === "text" ? (
                                <MarkdownMessage content={m.payload.response} />
                            ) : (
                                <ChatRenderer message={m.payload} />
                            )}
                        </div>
                    );
                })}

                {loading && (
                    <div className="chat-bubble system loading">
                        Pensando...
                    </div>
                )}

                <div ref={bottomRef} />
            </div>

            <div className="chat-input-area">
                <input
                    disabled={!connected}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && send()}
                    placeholder={
                        connected
                            ? "Escribe tu consulta..."
                            : "Sin conexión"
                    }
                />

                <button
                    disabled={!connected || loading}
                    onClick={send}
                >
                    {loading ? "..." : "Enviar"}
                </button>
            </div>

        </div>
        :
        <div
            ref={chatHomeRef}
            className={`chat-home ${hasStarted ? "chat-active" : "chat-idle"}`}
            style={{ height: chatHomeHeight }}
        >
            <div className="chat-home-center">
                <div className="chat-home-messages" ref={messagesContainerRef} >
                    {messages.map((m, idx) => {
                        const isLast = idx === messages.length - 1;
                        const shouldType =
                            isLast && m.from === "system" && m.payload.type === "text";

                        return (
                            <div
                                key={m.id}
                                ref={isLast ? lastMessageRef : null}
                                className={`chat-bubble ${m.from}`}
                            >
                                {m.payload.type === "text" ? (
                                    m.from === "system" ? (
                                        shouldType ? (
                                            <TypingMessage text={m.payload.response} speed={15} />
                                        ) : (
                                            <MarkdownMessage content={m.payload.response} />
                                        )
                                    ) : (
                                        <MarkdownMessage content={m.payload.response} />
                                    )
                                ) : (
                                    <ChatRenderer message={m.payload} />
                                )}
                            </div>
                        );
                    })}


                    {loading && (
                        <div className="chat-bubble system">
                            <div className="typing-indicator">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>

                {!hasStarted && (
                    <h1 className="chat-home-title">¿En qué puedo ayudarte?</h1>
                )}


                <div className={`chat-home-input ${!connected ? "disabled" : ""}`}>
                    <button
                        className="chat-home-plus"
                        type="button"
                        aria-label="Opciones"
                        disabled={!connected || loading}
                    // onClick={() => useQuickAction("")}
                    >
                        +
                    </button>

                    <input
                        disabled={!connected || loading}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && send()}
                        placeholder={connected ? "Pregunta lo que quieras" : "Sin conexión"}
                    />

                    <button
                        className="chat-home-send"
                        type="button"
                        disabled={!connected || loading || !input.trim()}
                        onClick={send}
                        aria-label="Enviar"
                    >
                        ➤
                    </button>
                </div>

                {/* <div className="chat-home-actions">
                    {quickActions.map(a => (
                        <button
                            key={a.label}
                            className="chat-home-action"
                            type="button"
                            disabled={!connected || loading}
                            onClick={() => useQuickAction(a.prompt)}
                        >
                            <span className="chat-home-action-icon">{a.icon}</span>
                            <span className="chat-home-action-text">{a.label}</span>
                        </button>
                    ))}
                </div> */}

                <div className="chat-home-status">
                    <span className={`mt-1 dot ${connected ? "on" : "off"}`} />
                    {connected ? "Conectado" : "Sin conexión"}
                    {loading ? " • Pensando..." : ""}
                </div>
            </div>
        </div>
        ;
}
